/**
 * Client Detail Page
 *
 * Complete detail view for a single client record with tabs
 */

import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { persistedClientRecords } from '../../services/mockData/clients';
import { useClientInvoices } from '../../services/queries/invoiceQueries';
import { useCreditBlockFiles, useUploadCreditBlockFile, useCreditBlockFileCounts, getFileForDownload, getRelTypeKeyForUpload, type CreditBlockFileCategory } from '../../services/queries/creditBlockFileQueries';
import { getRecordById, deleteRecord } from '../../services/recordStorageService';
import { shouldUseGobodo, USE_GOBODO_API } from '../../utils/gobodoUtils';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailHeader } from '../../components/detail/DetailHeader';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { LayoutSection } from '../../components/detail/LayoutSection';
import { TasksTab } from '../../components/detail/TasksTab';
import { CasesTab } from '../../components/detail/CasesTab';
import { ActivityTab } from '../../components/detail/ActivityTab';
import { ClientModal } from '../../components/modals/ClientModal';
import { LogCallModal } from '../../components/modals/LogCallModal';
import { LogMeetingModal } from '../../components/modals/LogMeetingModal';
import { LogTaskModal } from '../../components/modals/LogTaskModal';
import { AddNoteModal } from '../../components/modals/AddNoteModal';
import { SendEmailModal } from '../../components/modals/SendEmailModal';
import { CreateInvoiceFromPricebookModal } from '../../components/modals/CreateInvoiceFromPricebookModal';
import { CreateSubscriptionFromPricebookModal } from '../../components/modals/CreateSubscriptionFromPricebookModal';
import { usePageLayout, usePageLayoutTabs } from '../../hooks/useObjectManager';
import { User, CheckSquare, Briefcase, Package, Mail, Paperclip, History, Plus, Upload, Download, Clock, Receipt, FileText, Repeat } from 'lucide-react';
import { HistoryTab } from '../../components/detail/HistoryTab';
import { useToast } from '../../contexts/ToastContext';

interface ClientDetailPageProps {
  id: string;
  onBack: () => void;
}

export const ClientDetailPage: React.FC<ClientDetailPageProps> = ({ id, onBack }) => {
  const { success } = useToast();
  const queryClient = useQueryClient();
  const layoutTabs = usePageLayoutTabs('Client__c');
  const tabs = React.useMemo(() => {
    const list = [...layoutTabs];
    const insertIdx = list.findIndex((t: { id: string }) => t.id === 'overview' || t.id === 'tab_001' || t.id === 'details') + 1;
    const at = insertIdx > 0 ? insertIdx : 1;
    list.splice(at, 0, { id: 'transactions', label: 'Transactions', icon: Receipt });
    return list;
  }, [layoutTabs]);
  const overviewTabId = tabs[0]?.id ?? 'overview';
  const [activeTab, setActiveTab] = useState(overviewTabId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showInvoiceFromPricebookModal, setShowInvoiceFromPricebookModal] = useState(false);
  const [showSubscriptionFromPricebookModal, setShowSubscriptionFromPricebookModal] = useState(false);

  const { data: client, isLoading } = useQuery({
    queryKey: ['client', id],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const record = await getRecordById('Client', id);
        if (!record) throw new Error('Client not found');
        return { ...record, name: record.name ?? record.label ?? (record as any).Name__c };
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const allClients = persistedClientRecords.get();
      const found = allClients.find(c => c.id === id);
      if (!found) throw new Error('Client not found');
      return found;
    }
  });

  // Fetch page layout (uses Record Pages assignment system)
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Client__c', client);
  const { data: transactions = [], isLoading: transactionsLoading } = useClientInvoices(id);
  const isFilesTab = activeTab === 'files' || activeTab === 'tab_004';
  const [clientFileCategoryFilter, setClientFileCategoryFilter] = useState<'all' | 'identification' | 'contracts'>('all');
  const { data: gobodoFiles = [], isLoading: filesLoading } = useCreditBlockFiles(
    id,
    isFilesTab,
    clientFileCategoryFilter as CreditBlockFileCategory
  );
  const { data: fileCounts } = useCreditBlockFileCounts(id, USE_GOBODO_API && isFilesTab);
  const uploadFileMutation = useUploadCreditBlockFile(id);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadFile = async (file: { id: string; downloadUrl?: string }) => {
    if (!USE_GOBODO_API) return;
    if (file.downloadUrl) {
      window.open(file.downloadUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const fileId = Number(file.id);
    if (Number.isNaN(fileId)) return;
    try {
      const meta = await getFileForDownload(fileId);
      if (meta.downloadUrl) {
        window.open(meta.downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (_err) {
      // Error logged in service
    }
  };

  const handleFilesAdded = async (fileList: File[]) => {
    if (!USE_GOBODO_API) return;
    const reltypekey = getRelTypeKeyForUpload(clientFileCategoryFilter as CreditBlockFileCategory);
    for (const file of fileList) {
      try {
        await uploadFileMutation.mutateAsync({ file, reltypekey });
      } catch (_err) {
        // Error is logged in service
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (selected?.length) handleFilesAdded(Array.from(selected));
    e.target.value = '';
  };

  const invoiceStatusColors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    overdue: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handleDelete = () => {
    const displayName = client?.name ?? (client as any)?.Name__c ?? client?.label ?? id;
    if (!window.confirm(`Are you sure you want to delete ${displayName}?`)) return;
    if (shouldUseGobodo()) {
      deleteRecord('Client__c', id)
        .then((ok) => {
          queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
          queryClient.invalidateQueries({ queryKey: ['clientListViewData'] });
          queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Client__c'] });
          success('Client deleted', `${displayName} has been deleted`);
          onBack();
        })
        .catch((err) => {
          console.error('[ClientDetailPage] Delete failed:', err);
          success('Error', `Failed to delete client: ${err?.message || 'Unknown error'}`);
        });
      return;
    }
    const allClients = persistedClientRecords.get();
    persistedClientRecords.set(allClients.filter(c => c.id !== id));
    queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
    queryClient.invalidateQueries({ queryKey: ['clientListViewData'] });
    success('Client deleted', `${displayName} has been deleted`);
    onBack();
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Clients">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!client) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Clients">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Client not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  // Status badge colors
  const statusColors: Record<string, string> = {
    Active: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    Inactive: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300',
    Pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    Suspended: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
  };

  const isOverview = ['overview', 'tab_001', 'details', 'tab_002'].includes(activeTab);
  const isTransactions = activeTab === 'transactions';
  const isActivity = ['activity', 'tab_003'].includes(activeTab);
  const isTasks = ['tasks'].includes(activeTab);
  const isCases = ['cases'].includes(activeTab);
  const isProducts = ['products'].includes(activeTab);
  const isFiles = ['files', 'tab_004'].includes(activeTab);
  const isHistory = ['history'].includes(activeTab);

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Clients">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <DetailHeader
          title={client.name as string}
          subtitle={`${client.industry || 'Client'} • Owned by ${client.accountOwner || client.owner}`}
          recordId={client.id as string}
          status={client.status as string}
          statusColor={statusColors[client.status as string] || statusColors.Active}
          onEdit={handleEdit}
          onDelete={handleDelete}
          objectName="Client__c"
          recordData={client as Record<string, any>}
        />

        {/* Tabs */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="p-6">
            {isOverview && (
              <div className="space-y-6">
                {loadingLayout ? (
                  // Loading state
                  <div className="space-y-6">
                    {[1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6"
                      >
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Render sections from page layout
                  pageLayout?.sections.map(section => (
                    <LayoutSection
                      key={section.id}
                      section={section}
                      objectApiName="Client__c"
                      record={client}
                    />
                  ))
                )}
              </div>
            )}

            {isTransactions && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Transactions
                  </h3>
                  {shouldUseGobodo() && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowInvoiceFromPricebookModal(true)}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <FileText className="w-4 h-4" />
                        Create invoice from pricebook
                      </button>
                      <button
                        onClick={() => setShowSubscriptionFromPricebookModal(true)}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        <Repeat className="w-4 h-4" />
                        Create subscription from pricebook
                      </button>
                    </div>
                  )}
                </div>
                {transactionsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">No transactions</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                      Invoices for this client will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-[#334155] border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Invoice Number
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Client
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Issue Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Due Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {transactions.map((inv) => (
                          <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-[#334155]">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Link
                                to={`/invoices/${inv.id}`}
                                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                {inv.invoiceNumber}
                              </Link>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {inv.clientName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  invoiceStatusColors[inv.status] ?? invoiceStatusColors.draft
                                }`}
                              >
                                {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: inv.currency,
                              }).format(inv.totalAmount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(inv.issueDate).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(inv.dueDate).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {isActivity && (
              <ActivityTab
                recordId={client.id as string}
                recordType="client"
                onLogActivity={(type) => {
                  if (type === 'call') setShowCallModal(true);
                  if (type === 'meeting') setShowMeetingModal(true);
                  if (type === 'task') setShowTaskModal(true);
                  if (type === 'note') setShowNoteModal(true);
                }}
              />
            )}

            {isTasks && (
              <TasksTab
                relatedToId={client.id as string}
                relatedToName={client.name as string}
                relatedToType="Client"
              />
            )}

            {isCases && (
              <CasesTab
                accountId={client.id as string}
                accountName={client.name as string}
              />
            )}

            {isProducts && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Products & Services
                  </h3>
                  <button className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <Plus className="w-4 h-4" />
                    Add Product
                  </button>
                </div>

                {/* Enrolled Products */}
                <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                    Enrolled Products
                  </h4>
                  <div className="space-y-3">
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900 dark:text-white">
                            Credit Report Monitoring
                          </h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Active since Jan 2024
                          </p>
                        </div>
                        <span className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                          Active
                        </span>
                      </div>
                      <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                        $29.99/month
                      </div>
                    </div>
                  </div>
                </div>

                {/* Upsell Opportunities */}
                <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                    Upsell Opportunities
                  </h4>
                  <div className="space-y-3">
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900 dark:text-white">
                            Credit Score Improvement
                          </h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Recommended based on current enrollment
                          </p>
                        </div>
                        <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                          Offer
                        </button>
                      </div>
                      <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                        $49.99/month • +$20 MRR
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isFiles && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Files & Documents
                  </h3>
                  {USE_GOBODO_API && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadFileMutation.isPending}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <Upload className="w-4 h-4" />
                      {uploadFileMutation.isPending ? 'Uploading...' : 'Upload File'}
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileInputChange}
                  disabled={USE_GOBODO_API && uploadFileMutation.isPending}
                />
                {USE_GOBODO_API && uploadFileMutation.isError && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {uploadFileMutation.error instanceof Error ? uploadFileMutation.error.message : 'Upload failed'}
                  </p>
                )}

                {/* File categories: All Files uses all relationship types + CONTAINER; ID Documents and Contracts use their relationship types */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {(['all', 'identification', 'contracts'] as const).map((cat) => {
                    const label = cat === 'all' ? 'All Files' : cat === 'identification' ? 'ID Documents' : 'Contracts';
                    const isActive = clientFileCategoryFilter === cat;
                    const count = USE_GOBODO_API && fileCounts
                      ? fileCounts[cat]
                      : (cat === 'all' ? gobodoFiles.length : 0);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setClientFileCategoryFilter(cat)}
                        className={`p-4 rounded-lg text-left transition-colors ${
                          isActive
                            ? 'bg-white dark:bg-[#1e293b] border-2 border-blue-500'
                            : 'bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 hover:border-blue-500'
                        }`}
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{label}</div>
                        <div className={`text-2xl font-bold mt-2 ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                          {count}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* File list (Gobodo) */}
                {USE_GOBODO_API && gobodoFiles.length > 0 && (
                  <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-[#334155] border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">File Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Size</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Uploaded</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {gobodoFiles.map((file, idx) => (
                          <tr key={file.id || file.downloadUrl || `file-${idx}`} className="hover:bg-gray-50 dark:hover:bg-[#334155]">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{file.fileName}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 capitalize">{file.category}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{(file.fileSize / 1024).toFixed(1)} KB</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{new Date(file.uploadedDate).toLocaleDateString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => handleDownloadFile(file)}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* File upload area / empty state */}
                <div className="bg-white dark:bg-[#1e293b] rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 p-12">
                  <div className="text-center">
                    <Paperclip className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {filesLoading ? 'Loading files...' : USE_GOBODO_API && gobodoFiles.length > 0 ? 'Drop files to add more' : 'No files uploaded'}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Upload ID documents, contracts, invoices, and other client files.
                    </p>
                    {USE_GOBODO_API && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadFileMutation.isPending}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {uploadFileMutation.isPending ? 'Uploading...' : 'Upload Files'}
                      </button>
                    )}
                    {!USE_GOBODO_API && (
                      <p className="text-xs text-gray-500 dark:text-gray-500">Enable Gobodo API to upload and list files.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isHistory && (
              <HistoryTab recordId={id} objectType="Client__c" />
            )}
          </div>
        </div>
      </div>

      {/* Client Edit Modal */}
      <ClientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        client={client}
      />

      {/* Log Call Modal */}
      <LogCallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        recordId={client.id as string}
        recordType="client"
        contactName={client.name as string}
      />

      {/* Log Meeting Modal */}
      <LogMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        recordId={client.id as string}
        recordType="client"
      />

      {/* Log Task Modal */}
      <LogTaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        recordId={client.id as string}
        recordType="client"
        contactName={client.name as string}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        recordId={client.id as string}
        recordType="client"
      />

      {/* Send Email Modal */}
      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        recordId={client.id as string}
        recordType="client"
        recipientEmail={client.email as string}
        recipientName={client.name as string}
        emailContext={{ client: client as import('../../types').Client }}
      />

      {/* Create Invoice from Pricebook Modal */}
      <CreateInvoiceFromPricebookModal
        isOpen={showInvoiceFromPricebookModal}
        onClose={() => setShowInvoiceFromPricebookModal(false)}
        clientId={id}
        clientName={client.name as string}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['clientInvoices', id] });
          queryClient.invalidateQueries({ queryKey: ['invoices'] });
          queryClient.invalidateQueries({ queryKey: ['invoiceListViewData'] });
        }}
      />

      {/* Create Subscription from Pricebook Modal */}
      <CreateSubscriptionFromPricebookModal
        isOpen={showSubscriptionFromPricebookModal}
        onClose={() => setShowSubscriptionFromPricebookModal(false)}
        clientId={id}
        clientName={client.name as string}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['clientInvoices', id] });
        }}
      />
    </DetailPageLayout>
  );
};
